import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Link, useNavigate } from 'react-router-dom';
import { getTrainingHome, GetTrainingHomeOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, Play, Lock, ChevronRight, ArrowLeft, GraduationCap, ClipboardCheck, AlertTriangle, BookOpen, Settings, Shield, Award, FileText, HelpCircle } from 'lucide-react';
import { useFeatureToggles } from '@/components/FeatureToggleProvider';
import { Button } from '@/components/ui/button';

type Enrollment = GetTrainingHomeOutputType['enrollments'][0];
type ModuleItem = Enrollment['modules'][0];

function StatCard({ value, label, accent }: { value: number; label: string; accent?: boolean }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3 text-center">
      <p className={`text-2xl font-bold ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</p>
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  );
}

function ModuleRow({ mod, enrollmentId, useV2 }: { mod: ModuleItem; enrollmentId: string; useV2?: boolean }) {
  const isApproved = mod.status === 'approved';
  const isCurrent = mod.status === 'current' || mod.status === 'in_progress';
  const isLocked = mod.status === 'locked';
  const isFurtherTraining = mod.status === 'further_training';
  const basePath = `/train/${enrollmentId}/module/${mod.id}`;
  const modulePath = useV2 ? `${basePath}/v2` : basePath;

  return (
    <Link
      to={isLocked ? '#' : modulePath}
      className={`flex items-center gap-3 p-3 bg-card border rounded-xl transition-all touch-manipulation ${
        isCurrent ? 'border-primary/30' : 'border-border'
      } ${isLocked ? 'opacity-50 pointer-events-none' : 'active:scale-[0.98]'}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
        isApproved ? 'bg-primary/20' : isCurrent ? 'bg-accent/20' : isFurtherTraining ? 'bg-destructive/20' : 'bg-muted'
      }`}>
        {isApproved ? <CheckCircle className="w-4 h-4 text-primary" /> :
         isCurrent ? <Play className="w-4 h-4 text-accent-foreground" /> :
         isFurtherTraining ? <AlertTriangle className="w-4 h-4 text-destructive" /> :
         <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{mod.displayLabel || `Module ${mod.displayOrder}`}</p>
        <p className={`text-xs ${
          isApproved ? 'text-primary' : isCurrent ? 'text-primary' : isFurtherTraining ? 'text-destructive' : 'text-muted-foreground'
        }`}>
          {isApproved ? 'Approved' : isCurrent ? 'In progress' : isFurtherTraining ? 'Further training required' : 'Complete previous module first'}
        </p>
      </div>
      {!isLocked && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
    </Link>
  );
}

function CourseCard({ enrollment }: { enrollment: Enrollment }) {
  const progress = enrollment.totalModules > 0 ? Math.round((enrollment.completedModules / enrollment.totalModules) * 100) : 0;
  const isComplete = enrollment.status === 'Course Complete';
  const currentMod = enrollment.modules.find(m => m.status === 'current' || m.status === 'in_progress');

  return (
    <div className="border border-border bg-card rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
          isComplete ? 'bg-primary/20 text-primary' :
          enrollment.status === 'Assessment Requested' ? 'bg-orange-500/20 text-orange-600' :
          'bg-primary/10 text-primary'
        }`}>
          {isComplete ? 'Complete' : enrollment.status === 'Assigned' ? 'Assigned' : enrollment.status === 'Assessment Requested' ? 'Awaiting Assessment' : 'In Progress'}
        </span>
        <span className="text-xs text-muted-foreground">{enrollment.completedModules}/{enrollment.totalModules} modules</span>
      </div>

      <h2 className="font-bold text-foreground mb-0.5">{enrollment.courseTitle}</h2>
      <p className="text-xs text-muted-foreground mb-3">{enrollment.courseCode}</p>

      <div className="w-full h-2.5 bg-muted rounded-full mb-3 overflow-hidden">
        <div className="h-2.5 bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>

      {!isComplete && (
        <Link
          to={`/train/${enrollment.id}`}
          className="block w-full bg-primary text-primary-foreground text-sm font-semibold py-3 rounded-xl text-center active:scale-[0.98] transition-transform touch-manipulation"
        >
          {currentMod ? `Continue: ${currentMod.displayLabel || `Module ${currentMod.displayOrder}`}` : 'View Course'}
        </Link>
      )}
    </div>
  );
}

const ALL_NAV_ITEMS = [
  { label: 'Training', icon: GraduationCap, path: '/train', active: true, featureKey: null },
  { label: 'Policies', icon: Shield, path: '/my-policies', featureKey: null },
  { label: 'Certificates', icon: Award, path: '/certificates', featureKey: null },
  { label: 'Quiz History', icon: FileText, path: '/quiz-history', featureKey: 'quiz-history' },
  { label: 'Help', icon: HelpCircle, path: '/ask', featureKey: null },
];

export default function TrainingHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<GetTrainingHomeOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  const isAdmin = ['Admin', 'Owner', 'Manager'].includes(user?.role || '');
  const { isEnabled } = useFeatureToggles();
  const NAV_ITEMS = ALL_NAV_ITEMS.filter(item => !item.featureKey || isEnabled(item.featureKey));

  useEffect(() => {
    if (!user) return;
    getTrainingHome({})
      .then(d => {
        setData(d);
        if (d.enrollments.length > 0) setExpandedCourse(d.enrollments[0].id);
      })
      .catch(e => setError(e?.message || 'Failed to load training data'))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <Skeleton className="h-8 w-40" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <p className="text-xs text-muted-foreground">Welcome back</p>
              <h1 className="text-lg font-bold text-foreground">My Training</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link to="/train/admin" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                <Settings className="w-3.5 h-3.5" /> Admin
              </Link>
            )}
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
          </div>
        </div>
      </div>

      {/* Quick nav — wraps on mobile, no horizontal scroll */}
      <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2">
        {NAV_ITEMS.map(item => (
          <Link key={item.path} to={item.path}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              item.active ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}>
            <item.icon className="w-3.5 h-3.5" /> {item.label}
          </Link>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="px-4 pt-4">
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-destructive">Could not load training data</p>
              <p className="text-xs text-muted-foreground mt-1">{error}</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => { setError(''); setLoading(true); getTrainingHome({}).then(d => { setData(d); }).catch(e => setError(e?.message || 'Failed')).finally(() => setLoading(false)); }}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Stats */}
          <div className="px-4 pt-4 pb-2 grid grid-cols-3 gap-3">
            <StatCard value={data.stats.completed} label="Completed" />
            <StatCard value={data.stats.awaiting} label="Awaiting" accent />
            <StatCard value={data.stats.actions} label="Actions" />
          </div>

          {/* Enrolled courses */}
          {data.enrollments.length > 0 ? (
            <div className="px-4 py-3 space-y-4">
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">My Courses</h2>
              {data.enrollments.map(enrollment => (
                <div key={enrollment.id}>
                  <CourseCard enrollment={enrollment} />
                  <div className="mt-3">
                    <button
                      onClick={() => setExpandedCourse(expandedCourse === enrollment.id ? null : enrollment.id)}
                      className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 touch-manipulation"
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                      Modules ({enrollment.totalModules})
                      <ChevronRight className={`w-3 h-3 transition-transform ${expandedCourse === enrollment.id ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedCourse === enrollment.id && (
                      <div className="space-y-2">
                        {enrollment.modules.map(mod => (
                          <ModuleRow key={mod.id} mod={mod} enrollmentId={enrollment.id} useV2={isEnabled('interactive-course-player')} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-foreground mb-1">No courses assigned yet</h2>
              <p className="text-sm text-muted-foreground mb-4">When your manager assigns you a training course, it will appear here.</p>
              {isAdmin && (
                <Link to="/train/admin" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold">
                  <Settings className="w-4 h-4" /> Open Training Administration
                </Link>
              )}
            </div>
          )}

          {/* Assessment queue for managers */}
          {(data.isManager || data.isInvigilator) && data.assessmentQueue.length > 0 && (
            <div className="px-4 py-4 border-t border-border">
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <ClipboardCheck className="w-3.5 h-3.5" /> Assessment Queue ({data.assessmentQueue.length})
              </h2>
              <div className="space-y-2">
                {data.assessmentQueue.map(s => (
                  <Link key={s.sessionId} to={`/train/assess/${s.sessionId}`}
                    className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <ClipboardCheck className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{s.employeeName}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.moduleLabel} · {s.courseTitle}</p>
                    </div>
                    <span className="text-xs text-primary font-medium">{s.status}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
